
import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Clock, RefreshCw, AlertCircle } from 'lucide-react';

// ✅ همه قیمت‌ها از منبع واحد backend می‌آید (نه مستقیم از API خارجی)
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const LiveGoldPrice = () => {
  const [goldPrices, setGoldPrices] = useState({
    gold18: { price: 0, change: 0, changePercent: 0 },
    gold24: { price: 0, change: 0, changePercent: 0 },
    coin: { price: 0, change: 0, changePercent: 0 },
    ounce: { price: 0, change: 0, changePercent: 0 }
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // ✅ دریافت قیمت‌های طلا از backend (منبع واحد)
  const fetchGoldPrices = async (force = false) => {
    try {
      setLoading(true);
      setError(null);

      const url = `${API_BASE}/live-gold-prices/${force ? '?refresh=1' : ''}`;
      const response = await fetch(url, { signal: AbortSignal.timeout(10000) });

      if (!response.ok) throw new Error(`server returned ${response.status}`);

      const data = await response.json();
      console.log('Gold prices from backend:', data, 'source:', data._source);

      setGoldPrices({
        gold18: {
          price: Number(data.gold18?.price) || 0,
          change: Number(data.gold18?.change) || 0,
          changePercent: Number(data.gold18?.changePercent) || 0,
        },
        gold24: {
          price: Number(data.gold24?.price) || 0,
          change: Number(data.gold24?.change) || 0,
          changePercent: Number(data.gold24?.changePercent) || 0,
        },
        coin: {
          price: Number(data.coin?.price) || 0,
          change: Number(data.coin?.change) || 0,
          changePercent: Number(data.coin?.changePercent) || 0,
        },
        ounce: {
          price: Number(data.ounce?.price) || 0,
          change: Number(data.ounce?.change) || 0,
          changePercent: Number(data.ounce?.changePercent) || 0,
        }
      });

      // اگر هیچ منبعی در دسترس نبود
      if (data._source === 'unavailable') {
        setError('قیمت طلا فعلاً در دسترس نیست — لطفاً بعداً تلاش کنید.');
      }

      setLastUpdate(new Date());
    } catch (err) {
      console.error('Error fetching gold prices:', err);
      setError('اتصال به سرور با مشکل مواجه شد.');
    } finally {
      setLoading(false);
    }
  };

  // دریافت قیمت‌ها هنگام بارگذاری کامپوننت
  useEffect(() => {
    fetchGoldPrices();
    // به‌روزرسانی خودکار هر ۱ دقیقه (backend خودش 5 دقیقه cache داره)
    const interval = setInterval(() => fetchGoldPrices(false), 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // فرمت کردن قیمت
  const formatPrice = (price) => {
    return new Intl.NumberFormat('fa-IR').format(Math.round(price));
  };

  // فرمت کردن زمان
  const formatTime = (date) => {
    return new Intl.DateTimeFormat('fa-IR', {
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // رندر آیتم قیمت
  const PriceItem = ({ title, data, unit = 'تومان', isOunce = false }) => {
    const isPositive = data.change >= 0;
    
    return (
      <div className="bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition-shadow">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
          {isPositive ? (
            <TrendingUp className="w-4 h-4 text-green-500" />
          ) : (
            <TrendingDown className="w-4 h-4 text-red-500" />
          )}
        </div>
        
        <div className="mb-2">
          <span className="text-2xl font-bold text-gray-900">
            {isOunce ? `$${formatPrice(data.price)}` : formatPrice(data.price)}
          </span>
          <span className="text-sm text-gray-500 mr-2">{unit}</span>
        </div>
        
        <div className={`flex items-center text-sm ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
          <span className="font-semibold">
            {isPositive ? '+' : ''}{isOunce ? `$${formatPrice(data.change)}` : formatPrice(data.change)}
          </span>
          <span className="mr-1">({isPositive ? '+' : ''}{data.changePercent.toFixed(2)}%)</span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-amber-200 to-yellow-200 py-8">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center">
            <RefreshCw className="w-6 h-6 text-white animate-spin ml-3" />
            <span className="text-stone-800 font-semibold">در حال دریافت قیمت‌های لحظه‌ای...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex items-center justify-center py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 text-stone-800">
          <div className="flex items-center">
            <TrendingUp className="w-6 h-6 ml-2" />
            <h2 className="text-xl font-bold">قیمت لحظه‌ای طلا و سکه</h2>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center text-sm">
              <Clock className="w-4 h-4 ml-1" />
              <span>آخرین بروزرسانی: {formatTime(lastUpdate)}</span>
            </div>
            
            <button
              onClick={() => fetchGoldPrices(true)}
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="text-sm">بروزرسانی</span>
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 bg-orange-100 border border-orange-300 rounded-lg p-3 flex items-start">
            <AlertCircle className="w-5 h-5 text-orange-600 ml-2 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-orange-800">{error}</p>
          </div>
        )}

        {/* Price Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <PriceItem 
            title="طلای 18 عیار" 
            data={goldPrices.gold18}
            unit="تومان/گرم"
          />
          
          <PriceItem 
            title="طلای 24 عیار" 
            data={goldPrices.gold24}
            unit="تومان/گرم"
          />
          
          <PriceItem 
            title="سکه تمام بهار آزادی" 
            data={goldPrices.coin}
            unit="تومان"
          />
          
          <PriceItem 
            title="اونس جهانی طلا" 
            data={goldPrices.ounce}
            unit="دلار"
            isOunce={true}
          />
        </div>

        {/* Info Note */}
        <div className="mt-4 text-center">
          <p className="text-white/90 text-sm">
            قیمت‌ها به صورت خودکار هر 1 دقیقه بروزرسانی می‌شوند
          </p>
        </div>
      </div>
    </div>
  );
};

export default LiveGoldPrice;